import { LIMIT_KEYS, Plan, PlanFeatures, PlanLimits } from '../../shared/types';

/**
 * Turning a plan row into the strings the platform screens show.
 *
 * Kept out of the components because every one of these is a sentence someone
 * reads and acts on — "everything unlimited", "100 MB", "saves $288" — and a
 * wrong one is a commercial mistake, not a rendering glitch.
 */

/** Unlimited renders as the glyph, never as a number. */
export const INFINITY = '\u221e';

const COUNT = new Intl.NumberFormat('en-US');

/**
 * Money, in whole dollars when it is whole.
 *
 * $50 rather than $50.00 in a table of round prices; $99.50 keeps its cents.
 */
export function money(cents: number | null | undefined, currency = 'usd'): string {
  if (cents === null || cents === undefined) return '\u2014';
  const symbol = currency.toLowerCase() === 'usd' ? '$' : `${currency.toUpperCase()} `;
  const whole = cents % 100 === 0;
  return symbol + (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Storage, in the unit it was sold in.
 *
 * Decimal, not binary: a plan sold as "10 GB" stores 10,000,000,000, so the
 * editor's GB field is a round number and the customer's number matches ours.
 */
export function formatStorage(bytes: number | null): string {
  if (bytes === null) return INFINITY;
  if (bytes >= 1e9) return `${+(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${+(bytes / 1e6).toFixed(2)} MB`;
  if (bytes >= 1e3) return `${+(bytes / 1e3).toFixed(2)} kB`;
  return `${bytes} B`;
}

export const gbFromBytes = (bytes: number | null): number | null =>
  bytes === null ? null : +(bytes / 1e9).toFixed(6);

export const bytesFromGb = (gb: number | null): number | null =>
  gb === null ? null : Math.round(gb * 1e9);

export const LIMIT_LABEL: Record<keyof PlanLimits, string> = {
  forms: 'Forms',
  submissionsPerMonth: 'Submissions / month',
  users: 'Users',
  storageBytes: 'File storage',
  submissionRecords: 'Submission records kept',
  fieldsPerForm: 'Fields and widgets per form',
  aiGenerationsPerMonth: 'AI generations / month',
  aiAssistantMessagesPerMonth: 'AI assistant questions / month',
};

/**
 * Where each ceiling is enforced.
 *
 * Shown in the matrix so the number and the code that reads it stay findable
 * from one another. Each names a route that exists and refuses (review
 * finding 77: three named routes that never had).
 */
export const LIMIT_ENFORCED_AT: Record<keyof PlanLimits, string> = {
  forms: 'POST /api/admin/forms',
  submissionsPerMonth: 'POST /api/public/forms/:account/:slug/submissions',
  users: 'POST /api/admin/users',
  storageBytes: 'upload handler',
  submissionRecords: 'retention job, not a block',
  fieldsPerForm: 'publish',
  aiGenerationsPerMonth: 'POST /api/admin/forms/ai',
  aiAssistantMessagesPerMonth: 'POST /api/admin/forms/:id/assistant',
};

/** One limit, as the matrix and the editor show it. */
export function formatLimit(key: keyof PlanLimits, value: number | null): string {
  if (value === null) return INFINITY;
  return key === 'storageBytes' ? formatStorage(value) : COUNT.format(value);
}

const plural = (n: number, one: string, many: string) => `${COUNT.format(n)} ${n === 1 ? one : many}`;

/**
 * The one-line digest under a plan's name.
 *
 * Three numbers, because they are the three a customer asks about first. A
 * plan with no ceilings at all says so rather than printing three glyphs.
 */
export function limitsDigest(limits: PlanLimits, features: PlanFeatures): string {
  const sso = features?.sso ? ' \u00b7 SSO' : '';
  if (LIMIT_KEYS.every((k) => limits[k] === null)) return `everything unlimited${sso}`;
  const part = (v: number | null, one: string, many: string) =>
    v === null ? `${INFINITY} ${many}` : plural(v, one, many);
  return [
    part(limits.forms, 'form', 'forms'),
    limits.submissionsPerMonth === null ? `${INFINITY} subs/mo` : `${COUNT.format(limits.submissionsPerMonth)} subs/mo`,
    part(limits.users, 'user', 'users'),
  ].join(' \u00b7 ') + sso;
}

/**
 * Money with the cents always shown — "was $100.00 · not pushed", "$300.00".
 *
 * The price-history strings keep their cents even when they are zero, because
 * they sit next to Stripe amounts that always carry them.
 */
export function moneyExact(cents: number | null | undefined, currency = 'usd'): string {
  if (cents === null || cents === undefined) return '—';
  const symbol = currency.toLowerCase() === 'usd' ? '$' : `${currency.toUpperCase()} `;
  return symbol + (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/** The word the console's usage bar uses for each metric. */
export const USAGE_WORD: Record<keyof PlanLimits, string> = {
  forms: 'forms',
  submissionsPerMonth: 'submissions',
  users: 'users',
  storageBytes: 'storage',
  submissionRecords: 'records',
  fieldsPerForm: 'elements',
  aiGenerationsPerMonth: 'AI generations',
  aiAssistantMessagesPerMonth: 'AI assistant questions',
};

/** 1st, 2nd, 3rd attempt — the dunning sub-line counts like a person. */
export function ordinal(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
  return `${n}${suffix}`;
}

/** "9/18" — the shortest a date gets, inside a period like "8/19 → 9/18/2026". */
export const shortMD = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** "9/18/2026". */
export const shortMDY = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** "18 September 2026" — how the trial copy names the day the card is charged. */
export const longDate = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** Today as the plain calendar day the API speaks. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A calendar day plus n days, still as a calendar day. */
export function isoPlusDays(iso: string, n: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The calendar day an INSTANT falls on where the viewer is (074). The helpers
 * above read a stored date's day straight off the string, which is right for
 * a day (a free period's end) and a day out for an instant stored in UTC — a
 * deletion due at 02:26Z is still the evening before in Chicago.
 */
export function localDay(instant: string): string {
  const d = new Date(instant);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Whole days until an instant, rounded up, floored at zero — "30 days left to restore it". */
export function daysUntilInstant(instant: string, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(instant).getTime() - now) / 86400000));
}

/** Whole days from now until a calendar day, floored at zero. */
export function daysUntil(iso: string): number {
  const end = new Date(`${iso.slice(0, 10)}T23:59:59`).getTime();
  return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
}

/** Whole days between two calendar days. */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(`${fromIso.slice(0, 10)}T00:00:00`).getTime();
  const b = new Date(`${toIso.slice(0, 10)}T00:00:00`).getTime();
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * The saved-but-unpushed price change, if this plan has one.
 *
 * Editing a price never edits a Stripe Price — the local row moves first and
 * Stripe only follows on push. So a draft is exactly "the saved amount differs
 * from the amount the live Stripe Price carries" (or the sync stamp was
 * cleared). Free (no monthly price) and never-pushed plans have no draft:
 * "not pushed at all" is a different state with different copy.
 */
export function planDraft(plan: Pick<Plan,
  'price_monthly_cents' | 'pushed_monthly_cents' | 'price_annual_cents' | 'pushed_annual_cents' | 'stripe_product_id' | 'stripe_synced_at'
>): { newCents: number; wasCents: number | null } | null {
  if (plan.price_monthly_cents === null || !plan.stripe_product_id) return null;
  const pushedMonthly = plan.pushed_monthly_cents;
  const pushedAnnual = plan.pushed_annual_cents;
  const monthlyDiffers = pushedMonthly !== undefined && pushedMonthly !== null && pushedMonthly !== plan.price_monthly_cents;
  const annualDiffers = pushedAnnual !== undefined && pushedAnnual !== null && pushedAnnual !== plan.price_annual_cents;
  const stale = plan.stripe_synced_at === null;
  if (!monthlyDiffers && !annualDiffers && !stale) return null;
  return { newCents: plan.price_monthly_cents, wasCents: pushedMonthly ?? null };
}

/** What an annual subscriber keeps, against twelve months at the monthly rate. */
export function annualSavingCents(plan: Pick<Plan, 'price_monthly_cents' | 'price_annual_cents'>): number | null {
  if (plan.price_monthly_cents === null || plan.price_annual_cents === null) return null;
  return plan.price_monthly_cents * 12 - plan.price_annual_cents;
}

/**
 * The plan pill's colours.
 *
 * Keyed by the four seeded plans and falling back to neutral, so a plan
 * somebody adds later renders as a plan rather than as a missing style.
 */
export const PLAN_PILL: Record<string, { bg: string; fg: string }> = {
  free: { bg: '#edf2f7', fg: '#4a5568' },
  team: { bg: '#bee3f8', fg: '#2c5282' },
  enterprise_limited: { bg: '#e9d8fd', fg: '#553c9a' },
  enterprise_unlimited: { bg: '#553c9a', fg: '#ffffff' },
};

export const planPill = (key: string) => PLAN_PILL[key] || { bg: '#edf2f7', fg: '#4a5568' };

/** The 9px chip beside a plan name, by position rather than by key. */
const CHIPS = ['#a0aec0', '#4299e1', '#805ad5', '#553c9a'];
export const planChip = (index: number) => CHIPS[Math.min(index, CHIPS.length - 1)];

/**
 * What a discount sync did, as a sentence for the banner at the top of a
 * screen.
 *
 * Staff need three different facts from this and only one of them is "saved":
 * whether the customer's next bill actually changed, by how much, and — when it
 * did not — why. `applied: false` is the interesting case, because that is the
 * one where the console now shows a price the customer will never be charged.
 */
export function discountSentence(d: {
  applied: boolean;
  reason: string | null;
  warning: string | null;
  agreed: { amountOffCents: number; netCents: number } | null;
  credit: { amountOffCents: number; days: number; uncoveredCents: number } | null;
}): string {
  const parts: string[] = ['Saved.'];
  if (d.applied) {
    if (d.agreed) {
      parts.push(`Stripe now discounts ${money(d.agreed.amountOffCents)} a period, so they are charged ${money(d.agreed.netCents)}.`);
    }
    if (d.credit) {
      parts.push(`The ${d.credit.days}-day credit takes ${money(d.credit.amountOffCents)} off their next invoice.`);
      if (d.credit.uncoveredCents > 0) {
        // Only reachable on a short month — 30 days against a 28-day February.
        // Bounded, rare, and still never silent.
        parts.push(`${money(d.credit.uncoveredCents)} of it does not fit on one invoice and will NOT be credited — issue fewer days, or credit the rest in Stripe.`);
      }
    }
    if (!d.agreed && !d.credit) parts.push('Any discount on this account has been removed.');
  } else if (d.reason) {
    parts.push(d.reason);
  }
  if (d.warning) parts.push(d.warning);
  return parts.join(' ');
}
